import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog, Input, Button, LinearProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";

/**
 * 윈터 스쿨 등록 관리 > '결제링크 알림톡 발송' 팝업
 * 체크한 예약자들에게 결제 페이지(suneungsunbae.com/winterpay) 링크 알림톡('수능선배_윈터스쿨등록(사본)' 템플릿, 10/6 승인)을 보낸다.
 *  - 변수: 이름·등록기한·환불규정('입실 전 전액 환불', 서버 상수)·확인하기 버튼 링크. 등록기한 = 결제 가능한 마지막 시각
 *  - 입력은 '마감 시간(h)' 하나(기본 12, 1~168). 서버가 지금 + h 로 dueDate 와 등록기한 문구('M/D(요일) HH:mm')를 만들고, 링크 만료·만료 1시간 전 알림·결제 차단에 쓴다
 *  - 10/5 하루는 '윈터스쿨 결제 당일 ver1'(본문 날짜 고정)로 보냈고, 그 전 템플릿은 요약정보 칸(금액만 허용) 때문에 폐기됨
 *  - POST /dashboard/payments/winterpay/sendlinks 는 즉시 batchId 를 돌려주고 서버가 백그라운드로 발송 → GET /sendbatch 를 2초마다 폴링해 진행률 표시
 *  - '성공'은 Solapi 접수 기준. 서버가 발송 뒤 20초·90초에 카카오 실제 전송 결과를 확인해 거절(3xxx)이면 실패로 바꾸므로, 확인이 끝날 때까지(unverified 0) 폴링을 이어간다
 *  - 같은 예약 행에 다시 보내면 같은 링크(URL)를 재사용하고 발송 횟수만 올라간다. 실패 건은 횟수에 들어가지 않는다.
 * props: open, onClose, targets({id,name,phoneNumber,location}[]), onDone()
 */

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored",
};

const DEFAULT_DUE_HOURS = 12;
const DUE_HOURS_MIN = 1, DUE_HOURS_MAX = 168;
const REFUND_LABEL = "입실 전 전액 환불"; // 서버 SEND_REFUND_LABEL 과 같은 문구 (미리보기용)
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

// 마감 시각 문구 'M/D(요일) HH:mm' — 서버 dueLabelOf 와 같은 형식
const dueLabelOf = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}(${DAY_NAMES[d.getDay()]}) ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

// 템플릿 본문 — 미리보기용. 서버 alimtalk.ts winterRegisterLinkAlimtalk 와 같은 구성
const bodyPreview = (name: string, dueLabel: string) => `${name}님의 윈터스쿨 등록
수능선배에서 ${name}님의 윈터스쿨 등록비 명세서를 발송했어요.

이름 : ${name}
등록기한 : ${dueLabel}
환불규정 : ${REFUND_LABEL}

등록기한 내에만 등록 가능합니다.

[확인하기] → suneungsunbae.com/winterpay?id=…&code=…`;

const WinterSendLinkModal: React.FC<any> = (props) => {

    const [dueHours, setDueHours] = useState<string>(String(DEFAULT_DUE_HOURS)); // 마감 시간(h) — 지금 기준 몇 시간 뒤 마감
    const [now, setNow] = useState<number>(Date.now()); // 마감 시각 미리보기용 (1분마다 갱신)
    const [confirming, setConfirming] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [batch, setBatch] = useState<{ batchId: string, total: number, skipped: number } | null>(null);
    const [progress, setProgress] = useState<{ total: number, success: number, failed: number, pending: number, unverified: number } | null>(null);
    const timerRef = useRef<any>(null);

    const targets: any[] = props.targets || [];

    useEffect(() => {
        if (props.open) {
            setDueHours(String(DEFAULT_DUE_HOURS));
            setNow(Date.now());
            setConfirming(false);
            setLoading(false);
            setBatch(null);
            setProgress(null);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [props.open]);

    // 미리보기의 마감 시각이 멈춰 있지 않도록 1분마다 now 갱신
    useEffect(() => {
        if (!props.open) return;
        const t = setInterval(() => setNow(Date.now()), 60 * 1000);
        return () => clearInterval(t);
    }, [props.open]);

    const hours = +dueHours;
    const dueValid = Number.isInteger(hours) && hours >= DUE_HOURS_MIN && hours <= DUE_HOURS_MAX;
    const dueLabel = dueValid ? dueLabelOf(new Date(now + hours * 60 * 60 * 1000)) : "-";
    const canSend = targets.length > 0 && dueValid;
    const first = targets[0];
    const previewName = first ? first.name : "OOO";

    // 진행률 폴링 — pending 이 0이 되면 멈춘다
    const startPolling = (batchId: string) => {
        if (timerRef.current) clearInterval(timerRef.current);
        const tick = async () => {
            try {
                const r: any = await getFetch(`/dashboard/payments/winterpay/sendbatch?batchId=${batchId}`);
                if (r.message === "success") {
                    setProgress({ total: r.total, success: r.success, failed: r.failed, pending: r.pending, unverified: r.unverified || 0 });
                    if (r.pending === 0 && (r.unverified || 0) === 0 && timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                }
            } catch (e) {
                console.log(e);
            }
        };
        tick();
        timerRef.current = setInterval(tick, 2000);
    };

    const send = async () => {
        if (!canSend || loading) return;
        try {
            setLoading(true);
            const result: any = await postFetch("/dashboard/payments/winterpay/sendlinks", {
                prereserveIds: targets.map((t) => t.id),
                dueHours: hours,
            });
            if (result.message === "success") {
                setBatch({ batchId: result.batchId, total: result.total, skipped: result.skipped || 0 });
                if (result.total > 0) {
                    startPolling(result.batchId);
                } else {
                    setProgress({ total: 0, success: 0, failed: 0, pending: 0, unverified: 0 });
                }
            } else {
                console.log(result);
                toast.error(result.message === "wrongDate" ? `마감 시간은 ${DUE_HOURS_MIN}~${DUE_HOURS_MAX}시간 사이 정수여야 합니다` : "발송 요청에 실패했습니다. 다시 시도해 주세요.", toastOptions);
                setConfirming(false);
            }
        } catch (e) {
            console.log(e);
            toast.error("발송 요청에 실패했습니다. 다시 시도해 주세요.", toastOptions);
            setConfirming(false);
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (batch) props.onDone && props.onDone();
        props.onClose && props.onClose();
    };

    const sentAll = !!progress && progress.pending === 0;
    const done = sentAll && progress!.unverified === 0;
    const percent = progress && progress.total > 0 ? Math.round(((progress.success + progress.failed) / progress.total) * 100) : 0;

    const labelStyle: React.CSSProperties = { fontSize: "13px", marginBottom: "6px" };

    return (
        <Modal open={!!props.open} onClose={batch && !done ? undefined : close}>
            <ModalDialog>
                <div style={{ width: "420px", maxWidth: "92vw" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "6px" }}>
                        결제링크 알림톡 발송
                    </div>
                    <div style={{ fontSize: "13px", color: "#6B7684", marginBottom: "18px" }}>
                        선택한 {targets.length}명에게 '수능선배_윈터스쿨등록' 알림톡을 보냅니다. 버튼 '확인하기'로 결제 페이지에 들어갑니다.
                    </div>

                    {!batch && (
                        <>
                            <div style={labelStyle}>마감 시간(h) <span style={{ color: "#8B95A1" }}>— 지금부터 몇 시간 뒤까지 결제 가능한지 ({DUE_HOURS_MIN}~{DUE_HOURS_MAX})</span></div>
                            <Input
                                type="number"
                                value={dueHours}
                                onChange={(e) => setDueHours(e.target.value.replace(/[^0-9]/g, ""))}
                                slotProps={{ input: { min: DUE_HOURS_MIN, max: DUE_HOURS_MAX, step: 1 } }}
                                endDecorator="시간"
                                sx={{ marginBottom: "6px", width: "160px" }}
                                disabled={confirming}
                            />
                            <div style={{ fontSize: "13px", marginBottom: "14px", color: dueValid ? "#333D4B" : "#E8505B" }}>
                                {dueValid ? <>마감 시각: <b>{dueLabel}</b> (알림톡 '등록기한'에 이 문구가 들어가고, 마감 1시간 전 알림이 나갑니다)</> : `${DUE_HOURS_MIN}~${DUE_HOURS_MAX} 사이 정수를 입력해 주세요`}
                            </div>

                            <div style={labelStyle}>알림톡 미리보기 <span style={{ color: "#8B95A1" }}>(이름은 첫 번째 대상 기준)</span></div>
                            <div style={{ background: "#F5F6F8", borderRadius: "8px", padding: "12px 14px", fontSize: "12px", lineHeight: 1.6, color: "#333D4B", whiteSpace: "pre-wrap", marginBottom: "6px" }}>
                                {bodyPreview(previewName, dueLabel)}
                            </div>

                            {confirming && (
                                <div style={{ fontSize: "13px", color: "#E8505B", fontWeight: 600, marginTop: "10px" }}>
                                    {targets.length}명에게 실제로 알림톡이 발송됩니다. 정말 보낼까요?
                                </div>
                            )}

                            <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
                                <Button variant="outlined" color="neutral" fullWidth onClick={confirming ? () => setConfirming(false) : close} disabled={loading}>
                                    {confirming ? "아니오" : "취소"}
                                </Button>
                                {confirming ? (
                                    <Button variant="solid" color="danger" fullWidth onClick={send} loading={loading} disabled={!canSend}>
                                        네, {targets.length}명에게 발송
                                    </Button>
                                ) : (
                                    <Button variant="solid" fullWidth onClick={() => setConfirming(true)} disabled={!canSend}>
                                        발송하기 ({targets.length}명)
                                    </Button>
                                )}
                            </div>
                        </>
                    )}

                    {batch && (
                        <>
                            <div style={{ fontSize: "14px", marginBottom: "10px" }}>
                                {done ? "발송이 끝났습니다." : sentAll ? "카카오 전송 결과를 확인하는 중입니다 (최대 1분 30초). 창을 닫지 말고 잠시 기다려 주세요." : "발송 중입니다. 창을 닫지 말고 잠시 기다려 주세요."}
                            </div>
                            <LinearProgress determinate value={done ? 100 : percent} sx={{ marginBottom: "12px" }} />
                            <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "#333D4B", marginBottom: "6px" }}>
                                <span>대상 {batch.total}명</span>
                                <span style={{ color: "#1B64DA" }}>성공 {progress ? progress.success : 0}</span>
                                <span style={{ color: "#E8505B" }}>실패 {progress ? progress.failed : 0}</span>
                                <span style={{ color: "#6B7684" }}>대기 {progress ? progress.pending : batch.total}</span>
                                {sentAll && !done && <span style={{ color: "#6B7684" }}>결과 확인 중 {progress!.unverified}</span>}
                            </div>
                            {batch.skipped > 0 && (
                                <div style={{ fontSize: "12px", color: "#6B7684" }}>번호 형식이 맞지 않아 건너뛴 행 {batch.skipped}건 (010으로 시작하는 11자리만 발송)</div>
                            )}
                            <div style={{ fontSize: "12px", color: "#6B7684", marginTop: "4px" }}>'성공'은 카카오 수신까지 확인된 건입니다(거절되면 실패로 바뀜). 실패 건은 발송 횟수에 들어가지 않으며, 표의 '결제링크 발송시각' 숫자를 눌러 건별 사유를 볼 수 있어요.</div>

                            <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
                                <Button variant="solid" fullWidth onClick={close} disabled={!done}>
                                    {done ? "닫기" : sentAll ? "결과 확인 중..." : "발송 중..."}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default WinterSendLinkModal;
